import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, Loader2, Play, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api, formatApiError } from "@/lib/api";

const RESERVED = new Set([
  "EYVO_DEMO_URL",
  "EYVO_COMPANY",
  "EYVO_USERNAME",
  "EYVO_PASSWORD",
  "run_timestamp",
]);

// R2-A1 pacing presets.  Per-step cost estimates calibrated against the
// R2-P1 v4 run (38 steps, fast≈45s, presentation≈3-4min):
//   fast         ~1.6s/step  (network + screenshot + tiny overhead)
//   normal       ~3.0s/step  (paced typing + 500ms settle)
//   presentation ~4.8s/step  (slow typing + 1500ms settle + smooth scroll)
const PACE_PRESETS = [
  {
    value: "fast",
    label: "Fast (smoke-run)",
    perStepMs: 1600,
    hint: "Instant typing, no settle pause. Best for CI / quick verification.",
  },
  {
    value: "normal",
    label: "Normal",
    perStepMs: 3000,
    hint: "Moderate typing speed, brief settle between steps.",
  },
  {
    value: "presentation",
    label: "Presentation (recommended)",
    perStepMs: 4800,
    hint: "Slow paced typing, 1.5s settle, visible cursor — best for recordings.",
  },
];

function fmtDuration(ms) {
  const s = Math.round(ms / 1000);
  if (s < 60) return `~${s}s`;
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return r === 0 ? `~${m}m` : `~${m}m ${r}s`;
}

/**
 * Opens a Run-Demo modal for a workflow.
 * Props:
 *  - workflow: a full WorkflowDetail-shaped object ({workflow_id, name, variables: [...]})
 *  - open, onOpenChange: dialog state
 */
export default function RunDemoModal({ workflow, open, onOpenChange }) {
  const navigate = useNavigate();

  const initialValues = useMemo(() => {
    const obj = {};
    (workflow?.variables || []).forEach((v) => {
      if (!RESERVED.has(v.key)) obj[v.key] = v.default ?? "";
    });
    return obj;
  }, [workflow]);

  const [values, setValues] = useState(initialValues);
  const [headless, setHeadless] = useState(true);
  const [forceLogout, setForceLogout] = useState(true);
  const [demoPace, setDemoPace] = useState(
    localStorage.getItem("eda.demo_pace") || "presentation",
  );
  const [showCursor, setShowCursor] = useState(
    localStorage.getItem("eda.show_cursor") !== "false",
  );
  // R2-Phase-2.5 auto-pipeline: workflow.auto_pipeline drives the default;
  // user can opt out per-run.  When OFF we send skip_auto_pipeline=true so
  // the executor doesn't fan out to narration → voiceover → stitch.
  const workflowAutoPipeline = workflow?.auto_pipeline !== false;
  const [autoPipeline, setAutoPipeline] = useState(workflowAutoPipeline);

  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setHeadless(true);
      setForceLogout(true);
      setDemoPace(localStorage.getItem("eda.demo_pace") || "presentation");
      setShowCursor(localStorage.getItem("eda.show_cursor") !== "false");
      setAutoPipeline(workflowAutoPipeline);
    }
  }, [open, initialValues, workflowAutoPipeline]);

  const totalSteps = workflow?.steps?.length || 0;
  const pacePreset =
    PACE_PRESETS.find((p) => p.value === demoPace) || PACE_PRESETS[2];
  const estimatedMs = Math.max(1, totalSteps) * pacePreset.perStepMs;

  const startMutation = useMutation({
    mutationFn: async () => {
      localStorage.setItem("eda.demo_pace", demoPace);
      localStorage.setItem("eda.show_cursor", showCursor ? "true" : "false");
      const body = {
        variables: values,
        options: {
          headless,
          force_logout: forceLogout,
          demo_pace: demoPace,
          show_cursor: showCursor,
          skip_auto_pipeline: !autoPipeline,
        },
      };
      return (
        await api.post(`/workflows/${workflow.workflow_id}/run`, body)
      ).data;
    },
    onSuccess: (run) => {
      toast.success(`Run started for "${workflow.name}"`);
      onOpenChange(false);
      navigate(`/runs/${run.run_id}`);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const required = (workflow?.variables || []).filter(
    (v) => v.required && !RESERVED.has(v.key),
  );
  const allRequiredFilled = required.every(
    (v) => (values[v.key] || "").toString().trim() !== "",
  );

  const userVars = (workflow?.variables || []).filter(
    (v) => !RESERVED.has(v.key),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="run-demo-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Play size={16} className="text-indigo-600" />
            Generate Video · {workflow?.name}
          </DialogTitle>
          <DialogDescription>
            Set the variables for this run, choose options, then start.
            Reserved Eyvo credentials are injected from the server&apos;s
            environment and cannot be set from the UI.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {userVars.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="run-modal-no-vars">
              This workflow has no user-configurable variables.
            </p>
          ) : (
            <div className="space-y-3" data-testid="run-modal-variables">
              {userVars.map((v) => (
                <div key={v.key} className="space-y-1">
                  <Label
                    htmlFor={`var-${v.key}`}
                    className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-600"
                  >
                    {v.label || v.key}
                    {v.required && (
                      <span className="text-rose-600" aria-label="required">
                        *
                      </span>
                    )}
                  </Label>
                  <Input
                    id={`var-${v.key}`}
                    value={values[v.key] ?? ""}
                    onChange={(e) =>
                      setValues((p) => ({ ...p, [v.key]: e.target.value }))
                    }
                    placeholder={v.default || v.key}
                    data-testid={`run-modal-var-${v.key}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div
            className="space-y-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3"
            data-testid="run-modal-pace-block"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label
                  htmlFor="opt-pace"
                  className="text-xs font-semibold uppercase tracking-wide text-indigo-900"
                >
                  Demo Pace
                </Label>
                <p className="text-[11px] text-slate-600">{pacePreset.hint}</p>
              </div>
              <Select value={demoPace} onValueChange={setDemoPace}>
                <SelectTrigger
                  id="opt-pace"
                  className="h-9 w-[200px] text-xs"
                  data-testid="run-modal-pace-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PACE_PRESETS.map((p) => (
                    <SelectItem
                      key={p.value}
                      value={p.value}
                      data-testid={`run-modal-pace-${p.value}`}
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div
              className="flex items-center justify-between gap-3 text-[11px] text-slate-600"
              data-testid="run-modal-estimated-duration"
            >
              <span className="flex items-center gap-1.5">
                <Clock size={12} className="text-indigo-600" />
                Estimated duration: <strong>{fmtDuration(estimatedMs)}</strong>
                <span className="text-slate-400">
                  ({totalSteps} step{totalSteps === 1 ? "" : "s"})
                </span>
              </span>
              {demoPace !== "fast" && (
                <label className="flex items-center gap-1.5">
                  <Switch
                    id="opt-show-cursor"
                    checked={showCursor}
                    onCheckedChange={setShowCursor}
                    data-testid="opt-show-cursor"
                  />
                  <span className="text-[11px] text-slate-700">
                    Show cursor
                  </span>
                </label>
              )}
            </div>
          </div>

          <div
            className="flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
            data-testid="run-modal-auto-pipeline-block"
          >
            <div className="flex flex-1 items-start gap-2">
              <Sparkles size={14} className="mt-0.5 shrink-0 text-emerald-700" />
              <div>
                <Label
                  htmlFor="opt-auto-pipeline"
                  className="text-sm font-medium text-emerald-900"
                >
                  Auto-generate narration, voiceover &amp; final video
                </Label>
                <p className="text-[11px] text-emerald-800/80">
                  When this run finishes successfully, automatically chain
                  GPT-5.2 narration → ElevenLabs voiceover → ffmpeg-stitched
                  MP4. Failures in any leg are logged but don&apos;t affect
                  the run&apos;s status.
                </p>
              </div>
            </div>
            <Switch
              id="opt-auto-pipeline"
              checked={autoPipeline}
              onCheckedChange={setAutoPipeline}
              data-testid="opt-auto-pipeline"
            />
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <OptionRow
              id="opt-headless"
              label="Headless"
              hint="Run the browser without showing a UI window."
              checked={headless}
              onCheckedChange={setHeadless}
            />
            <OptionRow
              id="opt-force-logout"
              label="Force-logout existing Eyvo session"
              hint="Recommended on for Eyvo workflows — auto-clears SESSION_CONFLICT."
              checked={forceLogout}
              onCheckedChange={setForceLogout}
            />
          </div>

          <div
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
            data-testid="run-modal-reserved-notice"
          >
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              <code className="font-mono">EYVO_USERNAME</code>,{" "}
              <code className="font-mono">EYVO_PASSWORD</code>,{" "}
              <code className="font-mono">EYVO_COMPANY</code>, and{" "}
              <code className="font-mono">EYVO_DEMO_URL</code> are reserved
              environment variables — injected at run-time, redacted in all
              outputs.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={startMutation.isPending}
            data-testid="run-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => startMutation.mutate()}
            disabled={!allRequiredFilled || startMutation.isPending}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300"
            data-testid="run-modal-start"
          >
            {startMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Play size={14} />
            )}
            Start Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OptionRow({ id, label, hint, checked, onCheckedChange }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <Label htmlFor={id} className="text-sm font-medium text-slate-800">
          {label}
        </Label>
        <p className="text-[11px] text-slate-500">{hint}</p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        data-testid={id}
      />
    </div>
  );
}
